import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Calendar, Plus, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { safeFetchJson } from "@/utils/safeFetch";
import EventCard from "@/components/EventCard";
import CreateEventModal from "@/components/CreateEventModal";
import EventParticipantsModal from "@/components/EventParticipantsModal";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function EventsSection({ 
  userRole = "talent", // "talent" | "enterprise" | "trainer"
  showCreateButton = true,
  compact = false,
  maxEvents = 10,
  title = "Events"
}) {
  const [loading, setLoading] = useState(true);
  const [hostEvents, setHostEvents] = useState([]);
  const [participantEvents, setParticipantEvents] = useState([]);
  const [activeTab, setActiveTab] = useState(userRole === "talent" ? "invited" : "hosted");
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editEvent, setEditEvent] = useState(null);
  const [participantsEvent, setParticipantsEvent] = useState(null);  // For viewing participants
  
  const canHost = userRole === "enterprise" || userRole === "trainer";

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem("token");
      
      // Fetch participant events (for all users)
      const participantRes = await safeFetchJson(`${API_URL}/api/events/participant`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (participantRes.ok) {
        setParticipantEvents(participantRes.data || []);
      }
      
      // Fetch hosted events (only for enterprise/trainer)
      if (canHost) {
        const hostRes = await safeFetchJson(`${API_URL}/api/events/host`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        if (hostRes.ok) {
          setHostEvents(hostRes.data || []);
        }
      }
    } catch (error) {
      console.error("Error fetching events:", error);
      toast.error("Failed to load events");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEvents();
  }, [canHost]);

  const handleEventCreated = (newEvent) => {
    setHostEvents(prev => [newEvent, ...prev]);
  };

  const handleEventUpdated = (updatedEvent) => {
    setHostEvents(prev => prev.map(e => 
      e.event_id === updatedEvent.event_id ? updatedEvent : e
    ));
    setEditEvent(null);
  };

  const handleCancelEvent = async (event) => {
    if (!window.confirm("Are you sure you want to cancel this event?")) return;
    
    try {
      const token = localStorage.getItem("token");
      const parsed = await safeFetchJson(`${API_URL}/api/events/${event.event_id}/cancel`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!parsed.ok) {
        throw new Error(parsed.data?.detail || "Failed to cancel event");
      }
      
      toast.success("Event cancelled");
      fetchEvents();
    } catch (error) {
      toast.error(error.message);
    }
  };

  const handleAccept = (updatedEvent) => {
    setParticipantEvents(prev => prev.map(e => 
      e.event_id === updatedEvent.event_id ? updatedEvent : e
    ));
  };

  const handleDecline = (updatedEvent) => {
    setParticipantEvents(prev => prev.map(e => 
      e.event_id === updatedEvent.event_id ? updatedEvent : e
    ));
  };

  // Filter events
  const invitedEvents = participantEvents.filter(e => e.my_participation?.status === "invited");
  const upcomingEvents = participantEvents.filter(e => 
    e.my_participation?.status === "accepted" && 
    new Date(e.start_at) > new Date()
  );

  if (loading) {
    return (
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <div className="flex items-center justify-center py-8">
          <Loader2 className="w-6 h-6 animate-spin text-violet-600" />
          <span className="ml-2 text-gray-500">Loading events...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm" data-testid="events-section">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Calendar className="w-5 h-5 text-violet-600" />
          <h2 className="font-semibold text-lg text-gray-900">{title}</h2>
        </div>
        <div className="flex items-center gap-2">
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={fetchEvents}
            className="text-gray-500"
          >
            <RefreshCw className="w-4 h-4" />
          </Button>
          {canHost && showCreateButton && (
            <Button 
              size="sm"
              onClick={() => setShowCreateModal(true)}
              className="bg-violet-600 hover:bg-violet-700"
              data-testid="create-event-btn"
            >
              <Plus className="w-4 h-4 mr-1" />
              Create Event
            </Button>
          )}
        </div>
      </div>
      
      {/* Content */}
      <div className="p-6">
        {canHost ? (
          <Tabs value={activeTab} onValueChange={setActiveTab}>
            <TabsList className="mb-4">
              <TabsTrigger value="hosted">
                My Events ({hostEvents.length})
              </TabsTrigger>
              <TabsTrigger value="invited">
                Invitations ({invitedEvents.length})
              </TabsTrigger>
              <TabsTrigger value="upcoming">
                Upcoming ({upcomingEvents.length})
              </TabsTrigger>
            </TabsList>
            
            <TabsContent value="hosted" className="space-y-4">
              {hostEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No events created yet</p>
                  <Button 
                    className="mt-3 bg-violet-600 hover:bg-violet-700"
                    onClick={() => setShowCreateModal(true)}
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Create Your First Event
                  </Button>
                </div>
              ) : (
                hostEvents.slice(0, maxEvents).map(event => (
                  <EventCard
                    key={event.event_id}
                    event={event}
                    viewType="host"
                    compact={compact}
                    onEdit={setEditEvent}
                    onCancel={handleCancelEvent}
                    onViewParticipants={setParticipantsEvent}
                  />
                ))
              )}
            </TabsContent>
            
            <TabsContent value="invited" className="space-y-4">
              {invitedEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No pending invitations</p>
                </div>
              ) : (
                invitedEvents.slice(0, maxEvents).map(event => (
                  <EventCard
                    key={event.event_id}
                    event={event}
                    viewType="participant"
                    compact={compact}
                    onAccept={handleAccept}
                    onDecline={handleDecline}
                  />
                ))
              )}
            </TabsContent>
            
            <TabsContent value="upcoming" className="space-y-4">
              {upcomingEvents.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p>No upcoming events</p>
                </div>
              ) : (
                upcomingEvents.slice(0, maxEvents).map(event => (
                  <EventCard
                    key={event.event_id}
                    event={event}
                    viewType="participant"
                    compact={compact}
                    onAccept={handleAccept}
                    onDecline={handleDecline}
                  />
                ))
              )}
            </TabsContent>
          </Tabs>
        ) : (
          /* Talent view - simpler layout */
          <div className="space-y-4">
            {invitedEvents.length > 0 && (
              <div>
                <h3 className="font-medium text-gray-700 mb-3">Pending Invitations</h3>
                <div className="space-y-3">
                  {invitedEvents.slice(0, maxEvents).map(event => (
                    <EventCard
                      key={event.event_id}
                      event={event}
                      viewType="participant"
                      compact={compact}
                      onAccept={handleAccept}
                      onDecline={handleDecline}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {upcomingEvents.length > 0 && (
              <div className={invitedEvents.length > 0 ? "mt-6" : ""}>
                <h3 className="font-medium text-gray-700 mb-3">Upcoming Events</h3>
                <div className="space-y-3">
                  {upcomingEvents.slice(0, maxEvents).map(event => (
                    <EventCard
                      key={event.event_id}
                      event={event}
                      viewType="participant"
                      compact={compact}
                      onAccept={handleAccept}
                      onDecline={handleDecline}
                    />
                  ))}
                </div>
              </div>
            )}
            
            {invitedEvents.length === 0 && upcomingEvents.length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>No events to show</p>
                <p className="text-sm mt-1">Events you're invited to will appear here</p>
              </div>
            )}
          </div>
        )}
      </div>
      
      {/* Create/Edit Modal */}
      <CreateEventModal
        isOpen={showCreateModal || !!editEvent}
        onClose={() => {
          setShowCreateModal(false);
          setEditEvent(null);
        }}
        onSuccess={editEvent ? handleEventUpdated : handleEventCreated}
        userRole={userRole}
        editEvent={editEvent}
      />
      
      {/* Participants Modal */}
      <EventParticipantsModal
        isOpen={!!participantsEvent}
        onClose={() => setParticipantsEvent(null)}
        event={participantsEvent}
      />
    </div>
  );
}
